import { formatCurrency } from '@eriko/shared-types';
import { BarChart3 } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { formatPeriodLabel } from './utils';

interface CategoryItem {
  name: string;
  amount: number;
}

interface PnLSection {
  total: number;
  byCategory: CategoryItem[];
}

interface PnLData {
  type: string;
  period: string;
  currency?: string;
  revenue: PnLSection;
  expenses: PnLSection;
  netIncome: number;
}

function SectionRow({ item, currency }: { item: CategoryItem; currency: string }) {
  return (
    <div className="group flex items-center py-1.75 pl-11 pr-4.5 text-sm transition-colors hover:bg-surface-50">
      <span className="flex-1 text-surface-700 truncate mr-3">{item.name}</span>
      <span className="font-mono text-[13px] font-medium text-surface-500 text-right tabular-nums shrink-0 transition-colors group-hover:text-surface-800">
        {formatCurrency(item.amount, 2, currency)}
      </span>
    </div>
  );
}

export function PnlReport({ data }: { data: Record<string, unknown> }) {
  const pnl = data as unknown as PnLData;
  const periodLabel = formatPeriodLabel(pnl.period);
  const isPositive = pnl.netIncome >= 0;
  // Home currency from the tool payload so a UK/non-USD org shows £/€ not $ (CUR-1).
  const currency = pnl.currency ?? 'USD';

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-surface-100 px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <BarChart3 className="h-4.25 w-4.25 text-surface-400" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-surface-900">
            Profit & Loss
          </span>
        </div>
        {pnl.period && (
          <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
            {periodLabel}
          </span>
        )}
      </div>

      {/* Accordion sections */}
      <Accordion type="multiple" defaultValue={['revenue', 'expenses']}>
        {/* Revenue */}
        <AccordionItem value="revenue" className="border-b border-surface-100 last:border-b-0">
          <AccordionTrigger className="flex w-full items-center gap-2 px-4.5 py-2.5 text-left select-none transition-colors hover:bg-surface-50 hover:no-underline">
            <span className="flex-1 text-[13px] font-medium uppercase tracking-[0.04em] text-surface-700">
              Income
            </span>
            <span className="font-mono text-[13px] font-medium text-surface-700 tabular-nums shrink-0">
              {formatCurrency(pnl.revenue.total, 2, currency)}
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-0 pt-0">
            {pnl.revenue.byCategory.map((item) => (
              <SectionRow key={item.name} item={item} currency={currency} />
            ))}
          </AccordionContent>
        </AccordionItem>

        {/* Expenses */}
        <AccordionItem value="expenses" className="border-b border-surface-100 last:border-b-0">
          <AccordionTrigger className="flex w-full items-center gap-2 px-4.5 py-2.5 text-left select-none transition-colors hover:bg-surface-50 hover:no-underline">
            <span className="flex-1 text-[13px] font-medium uppercase tracking-[0.04em] text-surface-700">
              Expenses
            </span>
            <span className="font-mono text-[13px] font-medium text-surface-700 tabular-nums shrink-0">
              {formatCurrency(pnl.expenses.total, 2, currency)}
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-0 pt-0">
            {pnl.expenses.byCategory.map((item) => (
              <SectionRow key={item.name} item={item} currency={currency} />
            ))}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Net Income footer */}
      <div className="flex items-center border-t border-surface-200 px-4.5 py-2.5 text-sm font-semibold">
        <span className="flex-1 text-surface-900">Net Income</span>
        <span
          className={`font-mono text-sm font-semibold rounded px-2 py-0.5 ${
            isPositive
              ? 'text-pos bg-pos-surface'
              : 'text-neg bg-neg-surface'
          }`}
        >
          {formatCurrency(pnl.netIncome, 2, currency)}
        </span>
      </div>
    </div>
  );
}
